import { useState } from 'react';
import { message } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import {
  LoadingOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { updateOrgCover } from '../../redux/slices/authSlice';
import statusBarImg from '../../assets/statusbar.png';
import footerBarImg from '../../assets/footerbar.png';
import eventItemImg from '../../assets/eventitem.png';
import UploadItem from '../../components/UploadItem';
import SideNav from '../../components/SideNav';
import PageTransition from '../../components/PageTransition';

import request from '@/request';

export default function Index() {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [imageUrl, setImageUrl] = useState(user.org?.cover);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);

  // 处理图片上传
  const handleImageChange = (newImageUrl, file) => {
    let url = newImageUrl;
    // 只有 file 是 File/Blob 类型时才生成本地预览
    if (file instanceof File || (file && file instanceof Blob)) {
      url = URL.createObjectURL(file);
    }
    // 获取图片尺寸信息
    const img = new Image();
    img.src = url;
    img.onload = () => {
      setImageInfo({
        width: img.width,
        height: img.height,
        size: file?.size ? (file.size / 1024).toFixed(2) : '',
        ratio: (img.width / img.height).toFixed(2),
      });
    };
    setImageUrl(newImageUrl);
    setImageFile(file);
  };

  // 上传到服务器
  const handleSave = async () => {
    setUploading(true);
    try {
      await request.post('/user/org/info', { cover: imageUrl });
      // 更新 Redux store 中的 cover
      dispatch(updateOrgCover(imageUrl));
      message.success('封面更新成功！');
    } finally {
      setUploading(false);
    }
  };

  return (
    <PageTransition>
      <SideNav />

      <main className="flex-1 min-w-0">
        <div className="flex items-start justify-around bg-white rounded-xl shadow-sm p-6 gap-4">
          <div className="w-full space-y-6 flex flex-col items-center">
            <div className="w-full flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                设置主页封面
              </h2>
            </div>

            <UploadItem
              require={false}
              value={imageUrl}
              onChange={handleImageChange}
            />

            {/* 图片信息 */}
            {imageInfo && (
              <div className="w-75 mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircleOutlined className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    图片信息
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-600">尺寸：</span>
                    <span className="text-slate-900 font-medium">
                      {imageInfo.width} × {imageInfo.height}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-600">比例：</span>
                    <span className="text-slate-900 font-medium">
                      {imageInfo.ratio}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-600">大小：</span>
                    <span className="text-slate-900 font-medium">
                      {imageInfo.size} KB
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-600">格式：</span>
                    <span className="text-slate-900 font-medium">
                      {imageFile?.type?.split('/')[1]?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={uploading || !imageFile}
              className={`w-75 p-3 rounded-lg font-medium transition-all duration-200 ${
                uploading || !imageFile
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-500/30 cursor-pointer'
              }`}
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingOutlined />
                  更新中...
                </span>
              ) : (
                '更新封面'
              )}
            </button>

            {/* 使用指南 */}
            <div className="w-full bg-amber-50 rounded-xl border border-amber-200 p-5">
              <div className="flex items-start gap-3">
                <InfoCircleOutlined className="text-amber-600 text-lg mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 mb-2 text-left">
                    上传指南
                  </h3>
                  <ul className="text-sm text-amber-800 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      <span>
                        建议比例：<strong>1:2</strong>（例如 500×1000 像素）
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      <span>
                        支持格式：<strong>PNG、JPG、JPEG</strong>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      <span>
                        文件大小：<strong>不超过 5MB</strong>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：预览区域 */}
          <div className="w-full space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  小程序预览
                </h2>
              </div>
            </div>

            {/* 手机框架预览 */}
            <div className="flex justify-start md:justify-center items-end">
              <div className="relative">
                {/* 手机外框 (responsive smaller sizes to fit smaller screens) */}
                <div className="w-[320px] h-[680px] bg-slate-900 rounded-[3rem] p-2 shadow-2xl">
                  {/* 屏幕 */}
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    <img
                      src={statusBarImg}
                      alt="状态栏"
                      className="w-full absolute top-0"
                    />
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="预览"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
                        <svg
                          className="w-16 h-16 text-slate-300 mb-60"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                    <img
                      src={eventItemImg}
                      alt="赛事"
                      className="w-[96%] absolute bottom-22 left-2"
                    />
                    <img
                      src={footerBarImg}
                      alt="底部栏"
                      className="w-full absolute bottom-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PageTransition>
  );
}
